import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Calendar,
    Users,
    DollarSign,
    Mail,
    Clock,
    CheckCircle2,
    XCircle,
    Loader2,
    LogOut,
    Trash2,
    RefreshCw,
    CalendarPlus,
    FileText,
    Download,
    Dog,
    Phone,
    ChevronDown,
    ChevronUp,
    UserPlus,
    CalendarOff,
    Settings,
    Ticket,
    Percent,
    ToggleLeft,
    ToggleRight,
    Gift,
    BarChart3,
    CreditCard,
    Activity,
    X,
    Eye,
    AlertTriangle,
    Plus
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminAuth from '../components/admin/AdminAuth';
import AvailabilityManager from '../components/admin/AvailabilityManager';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import EmailLogsDashboard from '../components/admin/EmailLogsDashboard';
import SlotGenerator from '../components/admin/SlotGenerator';
import BookingsCalendar from '../components/admin/BookingsCalendar';
import BookingDiagnostics from '../components/admin/BookingDiagnostics';
import ManualBookingModal from '../components/admin/ManualBookingModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createAuthAxios = (token) => {
    return axios.create({
        baseURL: API,
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
};

const AdminPanel = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [token, setToken] = useState(null);
    const [adminUser, setAdminUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    
    // Data states
    const [stats, setStats] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [timeSlots, setTimeSlots] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [safetyForms, setSafetyForms] = useState([]);
    const [expandedForms, setExpandedForms] = useState({});
    const [waitlistEntries, setWaitlistEntries] = useState([]);
    const [blockedDates, setBlockedDates] = useState([]);
    const [existingBookingsWarning, setExistingBookingsWarning] = useState(null);
    const [discountCodes, setDiscountCodes] = useState([]);
    const [giftVouchers, setGiftVouchers] = useState([]);
    
    // Booking details modal state
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [bookingDetails, setBookingDetails] = useState(null);
    const [loadingBookingDetails, setLoadingBookingDetails] = useState(false);
    const [showManualBooking, setShowManualBooking] = useState(false);
    const [manualBookingContext, setManualBookingContext] = useState({});
    
    // Settings states
    const [showProfileSettings, setShowProfileSettings] = useState(false);
    const [profileForm, setProfileForm] = useState({
        name: '', email: '', currentPassword: '', newPassword: ''
    });
    
    // New slot form
    const [newSlot, setNewSlot] = useState({
        session_id: '', date: '', time: '', available_spots: 4
    });
    
    // New discount code form
    const [newDiscount, setNewDiscount] = useState({
        code: '', discount_type: 'percentage', discount_value: '', min_order: '', max_uses: ''
    });

    useEffect(() => {
        const savedToken = localStorage.getItem('adminToken');
        if (savedToken) verifyToken(savedToken);
    }, []);

    const verifyToken = async (savedToken) => {
        try {
            const authAxios = createAuthAxios(savedToken);
            const response = await authAxios.get('/admin/me');
            handleLoginSuccess(savedToken, response.data);
        } catch (error) {
            localStorage.removeItem('adminToken');
        }
    };

    const handleLoginSuccess = (newToken, admin) => {
        setToken(newToken);
        setAdminUser(admin);
        setIsAuthenticated(true);
        setProfileForm(prev => ({ ...prev, name: admin.name, email: admin.email }));
    };

    const handleLogout = () => {
        setIsAuthenticated(false);
        setToken(null);
        setAdminUser(null);
        localStorage.removeItem('adminToken');
        toast.success('Logged out');
    };

    useEffect(() => {
        if (isAuthenticated && token) fetchData();
    }, [isAuthenticated, activeTab, token]);

    const fetchData = async () => {
        if (!token) return;
        const authAxios = createAuthAxios(token);
        setLoading(true);
        
        try {
            const [statsRes, sessionsRes] = await Promise.all([
                authAxios.get('/admin/dashboard/stats'),
                axios.get(`${API}/sessions`)
            ]);
            setStats(statsRes.data);
            setSessions(sessionsRes.data);

            switch(activeTab) {
                case 'bookings':
                    const [bookingsRes, safetyFormsRes, timeSlotsForCalRes] = await Promise.all([
                        authAxios.get('/admin/bookings'),
                        authAxios.get('/admin/safety-forms'),
                        authAxios.get('/admin/time-slots')
                    ]);
                    setBookings(bookingsRes.data);
                    setSafetyForms(safetyFormsRes.data);
                    setTimeSlots(timeSlotsForCalRes.data);
                    break;
                case 'contacts':
                    setContacts((await authAxios.get('/admin/contacts')).data);
                    break;
                case 'slots':
                    setTimeSlots((await authAxios.get('/admin/time-slots')).data);
                    break;
                case 'transactions':
                    setTransactions((await authAxios.get('/admin/transactions')).data);
                    break;
                case 'safety':
                    setSafetyForms((await authAxios.get('/admin/safety-forms')).data);
                    break;
                case 'waitlist':
                    setWaitlistEntries((await authAxios.get('/admin/waitlist')).data);
                    break;
                case 'availability':
                    setBlockedDates((await authAxios.get('/admin/blocked-dates')).data);
                    break;
                case 'discounts':
                    setDiscountCodes((await authAxios.get('/admin/discount-codes')).data);
                    break;
                case 'vouchers':
                    setGiftVouchers((await authAxios.get('/admin/gift-vouchers')).data);
                    break;
                default:
                    break;
            }
        } catch (error) {
            if (error.response?.status === 401) {
                handleLogout();
                toast.error('Session expired');
            } else {
                toast.error('Failed to load data');
            }
        } finally {
            setLoading(false);
        }
    };

    // Availability Management
    const handleBlockDate = async (startDate, endDate, note) => {
        const authAxios = createAuthAxios(token);
        const response = await authAxios.post('/admin/blocked-dates', {
            start_date: startDate, end_date: endDate, note
        });
        
        if (response.data.has_existing_bookings) {
            setExistingBookingsWarning({
                blockId: response.data.blocked_date.id,
                bookings: response.data.existing_bookings
            });
        } else {
            toast.success('Date(s) blocked successfully');
        }
        fetchData();
    };

    const handleUnblockDate = async (blockId) => {
        if (!window.confirm('Unblock this date?')) return;
        const authAxios = createAuthAxios(token);
        await authAxios.delete(`/admin/blocked-dates/${blockId}`);
        toast.success('Date unblocked');
        fetchData();
    };

    const handleCancelBookings = async (blockId) => {
        const authAxios = createAuthAxios(token);
        await authAxios.post(`/admin/blocked-dates/${blockId}/cancel-bookings`);
        toast.success('Bookings cancelled');
        setExistingBookingsWarning(null);
        fetchData();
    };

    // Booking management
    const updateBookingStatus = async (bookingId, status) => {
        const authAxios = createAuthAxios(token);
        try {
            await authAxios.put(`/admin/bookings/${bookingId}`, { status });
            toast.success(`Booking ${status}`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update booking');
        }
    };

    const deleteBooking = async (bookingId, bookingData) => {
        if (!window.confirm('Are you sure you want to permanently delete this booking?')) return;
        const authAxios = createAuthAxios(token);
        
        const handleDeleteSuccess = (result) => {
            // Optimistic: remove from local state immediately
            setBookings(prev => prev.filter(b => b.id !== bookingId));
            
            // Update time slots state if slot info returned
            if (result && result.slot) {
                setTimeSlots(prev => prev.map(s => 
                    s.id === result.slot.slot_id 
                        ? { ...s, available_spots: result.slot.available_spots, booked_count: result.slot.booked_count, is_available: result.slot.is_available }
                        : s
                ));
            }
            
            setSelectedBooking(null);
            setBookingDetails(null);
            toast.success('Booking deleted successfully.');
            
            // Full refetch to sync all data
            fetchData();
        };
        
        try {
            const res = await authAxios.delete(`/admin/bookings/${bookingId}`);
            handleDeleteSuccess(res.data);
        } catch (error) {
            if (bookingData && bookingData.cart_id && bookingData.date && bookingData.time) {
                try {
                    const res2 = await authAxios.post('/admin/bookings/force-delete', {
                        cart_id: bookingData.cart_id,
                        date: bookingData.date,
                        time: bookingData.time,
                        session_name: bookingData.session_name || null
                    });
                    handleDeleteSuccess(res2.data);
                } catch (err2) {
                    toast.error(err2.response?.data?.detail || 'Failed to delete booking');
                }
            } else {
                toast.error(error.response?.data?.detail || 'Failed to delete booking');
            }
        }
    };

    const openBookingDetails = async (booking) => {
        setSelectedBooking(booking);
        setLoadingBookingDetails(true);
        
        // Find matching safety form for this booking
        const safetyForm = safetyForms.find(sf => sf.cart_id === booking.cart_id);
        setBookingDetails({
            ...booking,
            safetyForm: safetyForm || null
        });
        setLoadingBookingDetails(false);
    };

    const closeBookingDetails = () => {
        setSelectedBooking(null);
        setBookingDetails(null);
    };

    // Contact management
    const updateContactStatus = async (contactId, status) => {
        const authAxios = createAuthAxios(token);
        try {
            await authAxios.put(`/admin/contacts/${contactId}?status=${status}`);
            toast.success('Contact updated');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update contact');
        }
    };

    const deleteContact = async (contactId) => {
        if (!window.confirm('Delete this contact?')) return;
        const authAxios = createAuthAxios(token);
        try {
            await authAxios.delete(`/admin/contacts/${contactId}`);
            toast.success('Contact deleted');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete contact');
        }
    };

    // Time slot management
    const createTimeSlot = async (e) => {
        e.preventDefault();
        const authAxios = createAuthAxios(token);
        try {
            await authAxios.post('/admin/time-slots', newSlot);
            toast.success('Time slot created');
            setNewSlot({ session_id: '', date: '', time: '', available_spots: 4 });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create time slot');
        }
    };

    const deleteTimeSlot = async (slotId) => {
        if (!window.confirm('Delete this slot?')) return;
        const authAxios = createAuthAxios(token);
        try {
            await authAxios.delete(`/admin/time-slots/${slotId}`);
            toast.success('Time slot deleted');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete time slot');
        }
    };

    // Waitlist management
    const deleteWaitlistEntry = async (entryId) => {
        if (!window.confirm('Delete this waitlist entry?')) return;
        const authAxios = createAuthAxios(token);
        try {
            await authAxios.delete(`/admin/waitlist/${entryId}`);
            toast.success('Waitlist entry deleted');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete');
        }
    };

    // Discount code management
    const createDiscountCode = async (e) => {
        e.preventDefault();
        const authAxios = createAuthAxios(token);
        try {
            await authAxios.post('/admin/discount-codes', {
                code: newDiscount.code,
                discount_type: newDiscount.discount_type,
                discount_value: parseFloat(newDiscount.discount_value),
                min_order: newDiscount.min_order ? parseFloat(newDiscount.min_order) : 0,
                max_uses: newDiscount.max_uses ? parseInt(newDiscount.max_uses) : null
            });
            toast.success('Discount code created');
            setNewDiscount({ code: '', discount_type: 'percentage', discount_value: '', min_order: '', max_uses: '' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create discount code');
        }
    };

    const deleteDiscountCode = async (codeId) => {
        if (!window.confirm('Delete this discount code?')) return;
        const authAxios = createAuthAxios(token);
        await authAxios.delete(`/admin/discount-codes/${codeId}`);
        toast.success('Discount code deleted');
        fetchData();
    };

    const toggleDiscountCode = async (codeId) => {
        const authAxios = createAuthAxios(token);
        const response = await authAxios.put(`/admin/discount-codes/${codeId}/toggle`);
        toast.success(response.data.message);
        fetchData();
    };

    // CSV Export
    const exportSafetyForms = async () => {
        const authAxios = createAuthAxios(token);
        try {
            const response = await authAxios.get('/admin/safety-forms/export/csv', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = 'safety_forms_export.csv';
            link.click();
            toast.success('Export downloaded');
        } catch (error) {
            toast.error('Failed to export safety forms');
        }
    };

    // Profile update
    const handleProfileUpdate = async () => {
        const authAxios = createAuthAxios(token);
        const updateData = {};
        if (profileForm.name !== adminUser.name) updateData.name = profileForm.name;
        if (profileForm.email !== adminUser.email) updateData.email = profileForm.email;
        if (profileForm.newPassword) {
            updateData.current_password = profileForm.currentPassword;
            updateData.new_password = profileForm.newPassword;
        }
        
        await authAxios.put('/admin/profile', updateData);
        toast.success('Profile updated');
        setShowProfileSettings(false);
        if (updateData.name || updateData.email) {
            setAdminUser(prev => ({ ...prev, name: profileForm.name, email: profileForm.email }));
        }
        setProfileForm(prev => ({ ...prev, currentPassword: '', newPassword: '' }));
    };

    const toggleFormExpansion = (formId) => {
        setExpandedForms(prev => ({ ...prev, [formId]: !prev[formId] }));
    };

    // Login screen
    if (!isAuthenticated) {
        return <AdminAuth onLogin={handleLoginSuccess} />;
    }

    return (
        <div className="min-h-screen bg-secondary/30" data-testid="admin-dashboard">
            {/* Header */}
            <header className="bg-white border-b border-border sticky top-0 z-50">
                <div className="container-custom py-4 flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground font-serif">Admin Dashboard</h1>
                        <p className="text-sm text-muted-foreground">
                            Welcome, {adminUser?.name} • {adminUser?.email}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
                            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setShowProfileSettings(true)}>
                            <Settings className="w-4 h-4 mr-2" />
                            Settings
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                        </Button>
                    </div>
                </div>
            </header>

            {/* Profile Settings Modal */}
            {showProfileSettings && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-md">
                        <CardHeader>
                            <CardTitle>Account Settings</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <Label>Name</Label>
                                <Input value={profileForm.name} onChange={e => setProfileForm(p => ({...p, name: e.target.value}))} />
                            </div>
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={profileForm.email} onChange={e => setProfileForm(p => ({...p, email: e.target.value}))} />
                            </div>
                            <hr />
                            <p className="text-sm font-medium text-muted-foreground">Change Password</p>
                            <div>
                                <Label>Current Password</Label>
                                <Input type="password" value={profileForm.currentPassword} onChange={e => setProfileForm(p => ({...p, currentPassword: e.target.value}))} />
                            </div>
                            <div>
                                <Label>New Password</Label>
                                <Input type="password" value={profileForm.newPassword} onChange={e => setProfileForm(p => ({...p, newPassword: e.target.value}))} />
                            </div>
                            <div className="flex gap-2 pt-4">
                                <Button variant="outline" className="flex-1" onClick={() => setShowProfileSettings(false)}>Cancel</Button>
                                <Button className="flex-1 btn-primary" onClick={handleProfileUpdate}>Save</Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            <div className="container-custom py-8">
                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
                        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><Calendar className="w-5 h-5 text-blue-600" /></div><div><p className="text-2xl font-bold">{stats.total_bookings}</p><p className="text-xs text-muted-foreground">Bookings</p></div></div></CardContent></Card>
                        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><DollarSign className="w-5 h-5 text-green-600" /></div><div><p className="text-2xl font-bold">£{stats.total_revenue?.toFixed(0) || 0}</p><p className="text-xs text-muted-foreground">Revenue</p></div></div></CardContent></Card>
                        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><Mail className="w-5 h-5 text-amber-600" /></div><div><p className="text-2xl font-bold">{stats.new_contacts}</p><p className="text-xs text-muted-foreground">Messages</p></div></div></CardContent></Card>
                        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-purple-600" /></div><div><p className="text-2xl font-bold">{stats.available_slots}</p><p className="text-xs text-muted-foreground">Slots</p></div></div></CardContent></Card>
                        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center"><UserPlus className="w-5 h-5 text-red-600" /></div><div><p className="text-2xl font-bold">{stats.total_waitlist || 0}</p><p className="text-xs text-muted-foreground">Waitlist</p></div></div></CardContent></Card>
                        <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center"><CalendarOff className="w-5 h-5 text-gray-600" /></div><div><p className="text-2xl font-bold">{stats.blocked_dates_count || 0}</p><p className="text-xs text-muted-foreground">Blocked</p></div></div></CardContent></Card>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                    {['overview','analytics','emails','availability','bookings','safety','waitlist','contacts','slots','discounts','vouchers','transactions','diagnostics'].map(tab => (
                        <Button key={tab} variant={activeTab === tab ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab(tab)} className={activeTab === tab ? 'bg-primary' : ''}>
                            {tab === 'safety' ? 'Safety Forms' : tab === 'discounts' ? 'Discounts' : tab === 'vouchers' ? 'Vouchers' : tab === 'analytics' ? 'Analytics' : tab === 'emails' ? 'Email Logs' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </Button>
                    ))}
                </div>

                {loading ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
                ) : (
                    <>
                        {/* Analytics Dashboard */}
                        {activeTab === 'analytics' && (
                            <AnalyticsDashboard token={token} />
                        )}

                        {/* Email Logs Dashboard */}
                        {activeTab === 'emails' && (
                            <EmailLogsDashboard token={token} />
                        )}

                        {/* Overview */}
                        {activeTab === 'overview' && stats && (
                            <div className="grid md:grid-cols-2 gap-6">
                                <Card><CardHeader><CardTitle>Summary</CardTitle></CardHeader><CardContent><div className="space-y-4">
                                    <div className="flex justify-between items-center p-3 bg-secondary/50 rounded-lg"><span>Confirmed</span><Badge className="bg-green-100 text-green-700">{stats.confirmed_bookings}</Badge></div>
                                    <div className="flex justify-between items-center p-3 bg-secondary/50 rounded-lg"><span>Pending</span><Badge className="bg-amber-100 text-amber-700">{stats.pending_bookings}</Badge></div>
                                </div></CardContent></Card>
                                <Card><CardHeader><CardTitle>Quick Actions</CardTitle></CardHeader><CardContent><div className="space-y-3">
                                    <Button className="w-full justify-start" variant="outline" onClick={() => setActiveTab('analytics')}><BarChart3 className="w-4 h-4 mr-2" />View Analytics</Button>
                                    <Button className="w-full justify-start" variant="outline" onClick={() => setActiveTab('emails')}><Mail className="w-4 h-4 mr-2" />Email Logs</Button>
                                    <Button className="w-full justify-start" variant="outline" onClick={() => setActiveTab('availability')}><CalendarOff className="w-4 h-4 mr-2" />Manage Availability</Button>
                                    <Button className="w-full justify-start" variant="outline" onClick={() => setActiveTab('bookings')}><Calendar className="w-4 h-4 mr-2" />View Bookings</Button>
                                </div></CardContent></Card>
                            </div>
                        )}

                        {/* Availability */}
                        {activeTab === 'availability' && (
                            <AvailabilityManager 
                                blockedDates={blockedDates}
                                onBlockDate={handleBlockDate}
                                onUnblockDate={handleUnblockDate}
                                onCancelBookings={handleCancelBookings}
                                existingBookingsWarning={existingBookingsWarning}
                                setExistingBookingsWarning={setExistingBookingsWarning}
                                loading={loading}
                            />
                        )}

                        {/* Bookings */}
                        {activeTab === 'bookings' && (
                            <div>
                                <div className="flex justify-end mb-3">
                                    <Button onClick={() => { setManualBookingContext({}); setShowManualBooking(true); }} className="bg-primary" data-testid="add-booking-btn">
                                        <Plus className="w-4 h-4 mr-2" /> Add Booking
                                    </Button>
                                </div>
                                <BookingsCalendar
                                    bookings={bookings}
                                    safetyForms={safetyForms}
                                    slots={timeSlots}
                                    sessions={sessions}
                                    onOpenBookingDetails={openBookingDetails}
                                    onUpdateStatus={updateBookingStatus}
                                    onDeleteBooking={deleteBooking}
                                    onAddBooking={function(ctx) { setManualBookingContext(ctx || {}); setShowManualBooking(true); }}
                                />
                            </div>
                        )}

                        {/* Safety Forms */}
                        {activeTab === 'safety' && (
                            <Card><CardHeader className="flex flex-row items-center justify-between"><CardTitle>Safety Forms</CardTitle><Button variant="outline" size="sm" onClick={exportSafetyForms}><Download className="w-4 h-4 mr-2"/>Export CSV</Button></CardHeader><CardContent>
                                {safetyForms.length === 0 ? <p className="text-center text-muted-foreground py-8">No forms</p> : (
                                    <div className="space-y-4">
                                        {safetyForms.map(form => (
                                            <div key={form.id} className="border rounded-lg overflow-hidden">
                                                <button onClick={() => toggleFormExpansion(form.id)} className="w-full p-4 bg-secondary/30 flex items-center justify-between hover:bg-secondary/50">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center"><Dog className="w-5 h-5 text-primary" /></div>
                                                        <div className="text-left"><p className="font-semibold">{form.handlerName} & {form.dogName}</p><p className="text-sm text-muted-foreground">{form.dogBreed}</p></div>
                                                    </div>
                                                    {expandedForms[form.id] ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}
                                                </button>
                                                {expandedForms[form.id] && (
                                                    <div className="p-4 bg-white space-y-4">
                                                        <div className="grid md:grid-cols-2 gap-4">
                                                            <div><h4 className="font-semibold text-sm">Details</h4><div className="text-sm space-y-1"><p>Handler: {form.handlerName}</p><p>Dog: {form.dogName}</p><p>Breed: {form.dogBreed}</p>{form.dogGender && <p>Gender: {form.dogGender}</p>}<p>Age: {form.dogAge}</p><p>Emergency: {form.emergencyContact}</p></div></div>
                                                            <div><h4 className="font-semibold text-sm">Health</h4><p className="text-sm bg-secondary/30 p-2 rounded">{form.healthConditions}</p></div>
                                                        </div>
                                                        <div><h4 className="font-semibold text-sm">Behaviour</h4><p className="text-sm bg-secondary/30 p-2 rounded">{form.generalBehaviour}</p></div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent></Card>
                        )}

                        {/* Waitlist */}
                        {activeTab === 'waitlist' && (
                            <Card><CardHeader><CardTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-red-600"/>Waitlist</CardTitle></CardHeader><CardContent>
                                {waitlistEntries.length === 0 ? <p className="text-center text-muted-foreground py-8">No entries</p> : (
                                    <div className="space-y-3">
                                        {waitlistEntries.map(e => (
                                            <div key={e.id} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                    <div><p className="font-semibold">{e.name}</p><div className="flex items-center gap-4 text-sm text-muted-foreground"><span className="flex items-center gap-1"><Mail className="w-3 h-3"/>{e.email}</span><span className="flex items-center gap-1"><Phone className="w-3 h-3"/>{e.phone}</span></div></div>
                                                    <div className="flex flex-col items-end gap-2"><Badge className="bg-red-100 text-red-700">{e.session_name}</Badge><p className="text-xs text-muted-foreground">{e.slot_date} at {e.slot_time}</p></div>
                                                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteWaitlistEntry(e.id)}><Trash2 className="w-4 h-4"/></Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent></Card>
                        )}

                        {/* Contacts */}
                        {activeTab === 'contacts' && (
                            <Card><CardHeader><CardTitle>Messages</CardTitle></CardHeader><CardContent>
                                {contacts.length === 0 ? <p className="text-center text-muted-foreground py-8">No messages</p> : (
                                    <div className="space-y-3">
                                        {contacts.map(c => (
                                            <div key={c.id} className="p-4 bg-secondary/30 rounded-lg">
                                                <div className="flex justify-between items-start mb-2"><p className="font-semibold">{c.name}</p><Badge className={c.status==='new'?'bg-blue-100 text-blue-700':'bg-gray-100 text-gray-700'}>{c.status}</Badge></div>
                                                <p className="text-sm text-muted-foreground mb-2">{c.email} • {c.phone}</p>
                                                <p className="text-sm bg-white p-3 rounded">{c.message}</p>
                                                <div className="flex gap-2 mt-3">
                                                    {c.status==='new' && <Button size="sm" variant="outline" onClick={() => updateContactStatus(c.id,'read')}>Mark Read</Button>}
                                                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteContact(c.id)}><Trash2 className="w-4 h-4"/></Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent></Card>
                        )}

                        {/* Time Slots */}
                        {activeTab === 'slots' && (
                            <div className="space-y-6">
                                {/* Slot Generator - NEW */}
                                <SlotGenerator 
                                    token={token} 
                                    sessions={sessions} 
                                    onSlotsGenerated={() => fetchData('slots')} 
                                />
                                
                                <div className="grid md:grid-cols-2 gap-6">
                                    <Card><CardHeader><CardTitle>Create Single Slot</CardTitle></CardHeader><CardContent>
                                        <form onSubmit={createTimeSlot} className="space-y-4">
                                            <div><Label>Session</Label><select value={newSlot.session_id} onChange={e => setNewSlot(p=>({...p,session_id:e.target.value}))} className="w-full px-4 py-2 border rounded-lg" required>
                                                <option value="">Select...</option>
                                                {sessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                            </select></div>
                                            <div><Label>Date</Label><Input type="date" value={newSlot.date} onChange={e => setNewSlot(p=>({...p,date:e.target.value}))} required/></div>
                                            <div><Label>Time</Label><Input type="time" value={newSlot.time} onChange={e => setNewSlot(p=>({...p,time:e.target.value}))} required/></div>
                                            <div><Label>Spots</Label><Input type="number" min="1" max="10" value={newSlot.available_spots} onChange={e => setNewSlot(p=>({...p,available_spots:parseInt(e.target.value)}))} required/></div>
                                            <Button type="submit" className="w-full btn-primary"><CalendarPlus className="w-4 h-4 mr-2"/>Create</Button>
                                        </form>
                                    </CardContent></Card>
                                    <Card><CardHeader><CardTitle>Existing Slots ({timeSlots.length} total)</CardTitle></CardHeader><CardContent>
                                        {timeSlots.length === 0 ? <p className="text-center text-muted-foreground py-8">No slots</p> : (
                                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                                {timeSlots.slice(0,30).map(slot => (
                                                    <div key={slot.id} className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg">
                                                        <div><p className="font-medium text-sm">{slot.date} {slot.time}</p><p className="text-xs text-muted-foreground">{slot.available_spots}/{slot.max_capacity || 4} spots • {sessions.find(s => s.id === slot.session_id)?.name || 'Unknown'}</p></div>
                                                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteTimeSlot(slot.id)}><Trash2 className="w-4 h-4"/></Button>
                                                    </div>
                                                ))}
                                                {timeSlots.length > 30 && (
                                                    <p className="text-center text-sm text-muted-foreground py-2">
                                                        +{timeSlots.length - 30} more slots
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </CardContent></Card>
                                </div>
                            </div>
                        )}

                        {/* Discounts */}
                        {activeTab === 'discounts' && (
                            <div className="grid md:grid-cols-2 gap-6">
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Ticket className="w-5 h-5" />
                                            Create Discount Code
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <form onSubmit={createDiscountCode} className="space-y-4">
                                            <div>
                                                <Label>Code</Label>
                                                <Input 
                                                    value={newDiscount.code} 
                                                    onChange={e => setNewDiscount(p => ({...p, code: e.target.value.toUpperCase()}))}
                                                    placeholder="e.g., SUMMER20"
                                                    required
                                                    data-testid="discount-code-input"
                                                />
                                            </div>
                                            <div>
                                                <Label>Discount Type</Label>
                                                <select 
                                                    value={newDiscount.discount_type}
                                                    onChange={e => setNewDiscount(p => ({...p, discount_type: e.target.value}))}
                                                    className="w-full px-4 py-2 border rounded-lg"
                                                >
                                                    <option value="percentage">Percentage (%)</option>
                                                    <option value="fixed">Fixed Amount (£)</option>
                                                </select>
                                            </div>
                                            <div>
                                                <Label>{newDiscount.discount_type === 'percentage' ? 'Discount (%)' : 'Discount (£)'}</Label>
                                                <Input 
                                                    type="number"
                                                    min="0"
                                                    max={newDiscount.discount_type === 'percentage' ? '100' : undefined}
                                                    step="0.01"
                                                    value={newDiscount.discount_value}
                                                    onChange={e => setNewDiscount(p => ({...p, discount_value: e.target.value}))}
                                                    placeholder={newDiscount.discount_type === 'percentage' ? 'e.g., 20' : 'e.g., 10.00'}
                                                    required
                                                    data-testid="discount-value-input"
                                                />
                                            </div>
                                            <div>
                                                <Label>Minimum Order (£) - Optional</Label>
                                                <Input 
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={newDiscount.min_order}
                                                    onChange={e => setNewDiscount(p => ({...p, min_order: e.target.value}))}
                                                    placeholder="e.g., 50"
                                                />
                                            </div>
                                            <div>
                                                <Label>Max Uses - Optional (leave empty for unlimited)</Label>
                                                <Input 
                                                    type="number"
                                                    min="1"
                                                    value={newDiscount.max_uses}
                                                    onChange={e => setNewDiscount(p => ({...p, max_uses: e.target.value}))}
                                                    placeholder="e.g., 100"
                                                />
                                            </div>
                                            <Button type="submit" className="w-full btn-primary" data-testid="create-discount-btn">
                                                <Ticket className="w-4 h-4 mr-2" />
                                                Create Discount Code
                                            </Button>
                                        </form>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Active Discount Codes</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        {discountCodes.length === 0 ? (
                                            <p className="text-center text-muted-foreground py-8">No discount codes</p>
                                        ) : (
                                            <div className="space-y-3 max-h-[500px] overflow-y-auto">
                                                {discountCodes.map(code => (
                                                    <div key={code.id} className={`p-4 rounded-lg border ${code.is_active ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <p className="font-bold text-lg font-mono">{code.code}</p>
                                                                <p className="text-sm text-muted-foreground">
                                                                    {code.discount_type === 'percentage' 
                                                                        ? `${code.discount_value}% off` 
                                                                        : `£${code.discount_value} off`}
                                                                    {code.min_order > 0 && ` (min £${code.min_order})`}
                                                                </p>
                                                                <p className="text-xs text-muted-foreground mt-1">
                                                                    Used: {code.uses_count}{code.max_uses ? `/${code.max_uses}` : ''} times
                                                                </p>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <Button 
                                                                    size="sm" 
                                                                    variant="outline"
                                                                    onClick={() => toggleDiscountCode(code.id)}
                                                                    className={code.is_active ? 'text-amber-600' : 'text-green-600'}
                                                                >
                                                                    {code.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                                                                </Button>
                                                                <Button 
                                                                    size="sm" 
                                                                    variant="outline" 
                                                                    className="text-red-600"
                                                                    onClick={() => deleteDiscountCode(code.id)}
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                        <Badge className={code.is_active ? 'bg-green-100 text-green-700 mt-2' : 'bg-gray-100 text-gray-700 mt-2'}>
                                                            {code.is_active ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* Vouchers */}
                        {activeTab === 'vouchers' && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Gift className="w-5 h-5" />
                                        Gift Vouchers
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {giftVouchers.length === 0 ? (
                                        <p className="text-center text-muted-foreground py-8">No gift vouchers purchased yet</p>
                                    ) : (
                                        <div className="space-y-4">
                                            {/* Stats Summary */}
                                            <div className="grid grid-cols-4 gap-4 mb-6">
                                                <div className="bg-secondary/30 rounded-lg p-4 text-center">
                                                    <p className="text-2xl font-bold">{giftVouchers.length}</p>
                                                    <p className="text-sm text-muted-foreground">Total</p>
                                                </div>
                                                <div className="bg-green-50 rounded-lg p-4 text-center">
                                                    <p className="text-2xl font-bold text-green-600">{giftVouchers.filter(v => v.payment_status === 'paid').length}</p>
                                                    <p className="text-sm text-muted-foreground">Paid</p>
                                                </div>
                                                <div className="bg-blue-50 rounded-lg p-4 text-center">
                                                    <p className="text-2xl font-bold text-blue-600">{giftVouchers.filter(v => v.is_redeemed).length}</p>
                                                    <p className="text-sm text-muted-foreground">Redeemed</p>
                                                </div>
                                                <div className="bg-amber-50 rounded-lg p-4 text-center">
                                                    <p className="text-2xl font-bold text-amber-600">
                                                        £{giftVouchers.filter(v => v.payment_status === 'paid').reduce((sum, v) => sum + (v.amount || 0), 0).toFixed(0)}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">Revenue</p>
                                                </div>
                                            </div>
                                            
                                            {/* Vouchers List */}
                                            <div className="space-y-3 max-h-[500px] overflow-y-auto">
                                                {giftVouchers.map(voucher => (
                                                    <div key={voucher.id} className={`p-4 rounded-lg border ${voucher.is_redeemed ? 'bg-gray-50 border-gray-200' : 'bg-white border-border'}`}>
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <p className="font-mono font-bold text-lg">{voucher.code}</p>
                                                                <p className="text-sm text-muted-foreground">
                                                                    {voucher.voucher_type === 'session' ? voucher.session_name : `£${voucher.amount} voucher`}
                                                                </p>
                                                                <p className="text-xs text-muted-foreground mt-1">
                                                                    For: {voucher.recipient_name} ({voucher.recipient_email})
                                                                </p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    From: {voucher.purchaser_name}
                                                                </p>
                                                            </div>
                                                            <div className="flex flex-col items-end gap-2">
                                                                <Badge className={
                                                                    voucher.is_redeemed ? 'bg-blue-100 text-blue-700' :
                                                                    voucher.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                                                                    'bg-amber-100 text-amber-700'
                                                                }>
                                                                    {voucher.is_redeemed ? 'Redeemed' : voucher.payment_status}
                                                                </Badge>
                                                                <span className="text-lg font-bold text-primary">£{voucher.amount}</span>
                                                            </div>
                                                        </div>
                                                        {voucher.personal_message && (
                                                            <p className="text-sm italic text-muted-foreground mt-2 bg-secondary/30 p-2 rounded">
                                                                "{voucher.personal_message}"
                                                            </p>
                                                        )}
                                                        <p className="text-xs text-muted-foreground mt-2">
                                                            Created: {new Date(voucher.created_at).toLocaleDateString()}
                                                            {voucher.redeemed_at && ` • Redeemed: ${new Date(voucher.redeemed_at).toLocaleDateString()}`}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Transactions */}
                        {activeTab === 'transactions' && (
                            <Card><CardHeader><CardTitle>Transactions</CardTitle></CardHeader><CardContent>
                                {transactions.length === 0 ? <p className="text-center text-muted-foreground py-8">No transactions</p> : (
                                    <div className="space-y-3">
                                        {transactions.map(t => (
                                            <div key={t.id} className="p-4 bg-secondary/30 rounded-lg flex justify-between items-center">
                                                <div><p className="font-semibold">£{(t.amount/100).toFixed(2)}</p><p className="text-sm text-muted-foreground">{new Date(t.created_at).toLocaleDateString()}</p></div>
                                                <Badge className={t.payment_status==='paid'?'bg-green-100 text-green-700':'bg-amber-100 text-amber-700'}>{t.payment_status}</Badge>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent></Card>
                        )}

                        {activeTab === 'diagnostics' && (
                            <BookingDiagnostics authAxios={createAuthAxios(token)} />
                        )}
                    </>
                )}
            </div>

            {/* Booking Details Modal */}
            {selectedBooking && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="booking-details-modal">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-primary text-white p-6 flex justify-between items-start">
                            <div>
                                <h2 className="text-xl font-bold">Booking Details</h2>
                                <p className="text-white/80 text-sm">{selectedBooking.session_name}</p>
                            </div>
                            <button onClick={closeBookingDetails} className="p-2 hover:bg-white/10 rounded-full">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        
                        {loadingBookingDetails ? (
                            <div className="p-8 flex justify-center">
                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            </div>
                        ) : bookingDetails && (
                            <div className="p-6 space-y-6">
                                {/* Session Info */}
                                <div className="bg-secondary/30 rounded-lg p-4">
                                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                                        <Calendar className="w-5 h-5 text-primary" />
                                        Session Information
                                    </h3>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{bookingDetails.date}</span></div>
                                        <div><span className="text-muted-foreground">Time:</span> <span className="font-medium">{bookingDetails.time}</span></div>
                                        <div><span className="text-muted-foreground">Price:</span> <span className="font-medium text-primary">£{bookingDetails.price}</span></div>
                                        <div><span className="text-muted-foreground">Status:</span> <Badge className={bookingDetails.status==='confirmed'?'bg-green-100 text-green-700':bookingDetails.status==='cancelled'?'bg-red-100 text-red-700':'bg-amber-100 text-amber-700'}>{bookingDetails.status}</Badge></div>
                                    </div>
                                </div>

                                {/* Handler & Dog Info */}
                                {bookingDetails.safetyForm ? (
                                    <>
                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                            <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                                                <Dog className="w-5 h-5 text-amber-600" />
                                                Handler & Dog
                                            </h3>
                                            <div className="grid md:grid-cols-2 gap-4 text-sm">
                                                <div><span className="text-muted-foreground">Handler:</span> <span className="font-medium">{bookingDetails.safetyForm.handlerName}</span></div>
                                                <div><span className="text-muted-foreground">Dog Name:</span> <span className="font-medium">{bookingDetails.safetyForm.dogName}</span></div>
                                                <div><span className="text-muted-foreground">Breed:</span> <span className="font-medium">{bookingDetails.safetyForm.dogBreed}</span></div>
                                                <div><span className="text-muted-foreground">Age:</span> <span className="font-medium">{bookingDetails.safetyForm.dogAge}</span></div>
                                                {bookingDetails.safetyForm.dogGender && <div><span className="text-muted-foreground">Gender:</span> <span className="font-medium">{bookingDetails.safetyForm.dogGender}</span></div>}
                                                <div className="col-span-2"><span className="text-muted-foreground">Emergency Contact:</span> <span className="font-medium">{bookingDetails.safetyForm.emergencyContact}</span></div>
                                            </div>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h3 className="font-semibold text-lg mb-3">Health & Fitness</h3>
                                            <p className="text-sm bg-white p-3 rounded border">{bookingDetails.safetyForm.healthConditions || 'None specified'}</p>
                                        </div>

                                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                            <h3 className="font-semibold text-lg mb-3">Behaviour</h3>
                                            <p className="text-sm bg-white p-3 rounded border mb-2">{bookingDetails.safetyForm.generalBehaviour || 'Not specified'}</p>
                                            {bookingDetails.safetyForm.knownTriggers && (
                                                <div className="text-sm"><span className="text-muted-foreground">Known Triggers:</span> {bookingDetails.safetyForm.knownTriggers}</div>
                                            )}
                                        </div>

                                        {bookingDetails.safetyForm.additionalInfo && (
                                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                                <h3 className="font-semibold text-lg mb-3">Additional Information</h3>
                                                <p className="text-sm">{bookingDetails.safetyForm.additionalInfo}</p>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                                        <AlertTriangle className="w-5 h-5 text-red-600" />
                                        <p className="text-red-800">No safety form found for this booking</p>
                                    </div>
                                )}

                                {/* Actions */}
                                <div className="flex gap-3 pt-4 border-t">
                                    {bookingDetails.status === 'pending' && (
                                        <Button onClick={() => { updateBookingStatus(bookingDetails.id, 'confirmed'); closeBookingDetails(); }} className="flex-1">
                                            <CheckCircle2 className="w-4 h-4 mr-2" />
                                            Confirm Booking
                                        </Button>
                                    )}
                                    {bookingDetails.status !== 'cancelled' && (
                                        <Button variant="outline" onClick={() => { updateBookingStatus(bookingDetails.id, 'cancelled'); closeBookingDetails(); }}>
                                            <XCircle className="w-4 h-4 mr-2" />
                                            Cancel
                                        </Button>
                                    )}
                                    <Button variant="outline" className="text-red-600" onClick={() => deleteBooking(bookingDetails.id, bookingDetails)}>
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showManualBooking && (
                <ManualBookingModal
                    authAxios={createAuthAxios(token)}
                    sessions={sessions}
                    slots={timeSlots}
                    selectedDate={manualBookingContext.date || null}
                    selectedTime={manualBookingContext.time || null}
                    selectedSessionId={manualBookingContext.sessionId || ''}
                    onClose={() => setShowManualBooking(false)}
                    onSuccess={(result) => {
                        setShowManualBooking(false);
                        toast.success(`Booking added: ${result.session_name} on ${result.date}`);
                        fetchData();
                    }}
                />
            )}
        </div>
    );
};

export default AdminPanel;
